import produce from 'immer'
import { assign, filter, forEach, get, isEmpty, map, set, uniqBy } from 'lodash'

import {
  buildCategoriesTreeHierarchy,
  transformTree,
} from '../../../common/helpers'

import {
  ADD_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY_NODE,
  DELETE_LOCATION_TYPE,
  DELETE_LOCATION_TYPE_CATEGORY,
  EXPAND_LOCATION_TYPE_AND_LOCATION_TYPE_CATEGORY_MAIN_TREE_KEYS,
  EXPAND_LOCATION_TYPE_AND_LOCATION_TYPE_CATEGORY_SUPPORTING_TREE_KEYS,
  FETCH_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES,
  MOVE_LOCATION_TYPES,
  RENAME_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY_NODE,
  REPLACE_LOCATION_TYPE,
  RESET_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES_TREE,
  REVERT_REPLACE_LOCATION_TYPE,
  SAVE_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES,
  SET_DATA_FOR_RENAMING_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY_NODE,
  SET_IS_CURATION_ENABLED_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY,
} from '../action-types'
import { LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES_INITIAL_STATE } from '../initial-states'

const locationTypesAndLocationTypeCategoriesReducer = (
  state = LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES_INITIAL_STATE,
  action,
) => {
  switch (action.type) {
    case FETCH_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } =
          action.payload.data.locationTypesAndLocationTypeCategories

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          latestEvent,
        })
      })

    case FETCH_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES.CLEANUP:
      return { ...LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES_INITIAL_STATE }

    case SAVE_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          latestEvent: undefined,
        }),
      )

    case SAVE_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } =
          action.payload.data.saveLocationTypesAndLocationTypeCategories

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
          latestEvent,
        })
      })

    case SAVE_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES.FAIL:
      return produce(state, nextState => {
        const { graphQLErrors, message } = action.error
        const entities = get(graphQLErrors, '[0].extensions.exception.entities')

        return assign(nextState, {
          isLoading: false,
          error: {
            message,
            entities,
          },
        })
      })

    case DELETE_LOCATION_TYPE.START:
    case DELETE_LOCATION_TYPE_CATEGORY.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case DELETE_LOCATION_TYPE.COMPLETE:
    case DELETE_LOCATION_TYPE_CATEGORY.COMPLETE:
      return produce(state, nextState => {
        const { results } =
          action.payload.data.deleteLocationType ||
          action.payload.data.deleteLocationTypeCategory

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case DELETE_LOCATION_TYPE.FAIL:
    case DELETE_LOCATION_TYPE_CATEGORY.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case SET_IS_CURATION_ENABLED_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case SET_IS_CURATION_ENABLED_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY.COMPLETE:
      return produce(state, nextState => {
        const updatedLocationType =
          action.payload.data
            .setIsCurationEnabledLocationTypeOrLocationTypeCategory

        const treeData = transformTree(nextState.initialData, node =>
          node.id === updatedLocationType.id
            ? {
                ...node,
                isCurationEnabled: updatedLocationType.isCurationEnabled,
              }
            : node,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case SET_IS_CURATION_ENABLED_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case ADD_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY_NODE:
      return produce(state, nextState => {
        const { parentPath } = action.payload
        const isRootNodeAdded = isEmpty(parentPath)

        const arrayToAddNodeTo = isRootNodeAdded
          ? nextState.data
          : get(nextState.data, `${parentPath}.children`, [])

        arrayToAddNodeTo.push(action.payload)

        const treeDataWithNewNode = isRootNodeAdded
          ? arrayToAddNodeTo
          : set(nextState.data, `${parentPath}.children`, arrayToAddNodeTo)

        return assign(nextState, {
          ...nextState,
          data: treeDataWithNewNode,
          isDirty: true,
          dataForRenaming: { id: action.payload.id, type: action.payload.type },
        })
      })

    case RENAME_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY_NODE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: set(
            nextState.data,
            `${action.payload.path}.name`,
            action.payload.name,
          ),
          isDirty: true,
        }),
      )

    case SET_DATA_FOR_RENAMING_LOCATION_TYPE_OR_LOCATION_TYPE_CATEGORY_NODE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          dataForRenaming: action.payload,
        }),
      )

    case MOVE_LOCATION_TYPES:
      return produce(state, nextState => {
        const { nodesToMove, destinationNode } = action.payload
        let newTreeData = nextState.data

        // remove nodes to be moved
        forEach(nodesToMove, ({ id: idToMove, parentPath }) => {
          const isMovingRootNode = isEmpty(parentPath)

          if (isMovingRootNode) {
            newTreeData = filter(newTreeData, ({ id }) => id !== idToMove)
          } else {
            newTreeData = set(
              newTreeData,
              `${parentPath}.children`,
              filter(
                get(newTreeData, `${parentPath}.children`),
                ({ id }) => id !== idToMove,
              ),
            )
          }
        })

        const isMovedToRoot = destinationNode.level === 1

        if (isMovedToRoot) {
          newTreeData = buildCategoriesTreeHierarchy([
            ...newTreeData,
            ...map(nodesToMove, node => ({
              ...node,
              parentId: null,
              level: 1,
              idPath: [node.id],
              namePath: [node.name],
            })),
          ])
        } else {
          newTreeData = transformTree(newTreeData, node =>
            node.id === destinationNode.id
              ? {
                  ...node,
                  children: uniqBy(
                    [
                      ...(node.children || []),
                      ...map(nodesToMove, node => ({
                        ...node,
                        parentId: destinationNode.id,
                        level: destinationNode.level - 1,
                        idPath: [...destinationNode.idPath, node.id],
                        namePath: [...destinationNode.namePath, node.name],
                      })),
                    ],
                    'id',
                  ),
                }
              : node,
          )
        }

        return assign(nextState, {
          ...nextState,
          isDirty: true,
          data: newTreeData,
        })
      })

    case EXPAND_LOCATION_TYPE_AND_LOCATION_TYPE_CATEGORY_MAIN_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          mainTreeExpandedKeys: action.payload,
        }),
      )

    case EXPAND_LOCATION_TYPE_AND_LOCATION_TYPE_CATEGORY_SUPPORTING_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          supportingTreeExpandedKeys: action.payload,
        }),
      )

    case RESET_LOCATION_TYPES_AND_LOCATION_TYPE_CATEGORIES_TREE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          error: undefined,
          data: nextState.initialData,
          isDirty: false,
          dataForRenaming: undefined,
        }),
      )

    case REPLACE_LOCATION_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REPLACE_LOCATION_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.replaceLocationType

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REPLACE_LOCATION_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case REVERT_REPLACE_LOCATION_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REVERT_REPLACE_LOCATION_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.revertReplaceLocationType

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REVERT_REPLACE_LOCATION_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )
    default:
      return state
  }
}

export default locationTypesAndLocationTypeCategoriesReducer
